// src/utils/api.js
import axios from 'axios';

// Origin of the API — OAuth callback pages are served from here, so popup messages
// are checked against it.
export const API_ORIGIN = new URL(process.env.REACT_APP_API_URL || '/api', window.location.origin).origin;

// Auth is an httpOnly session cookie set by the API — no token is ever visible to
// page scripts. X-Requested-With is the API's CSRF check (backend src/index.js).
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
});

// Session expired or revoked: drop the cached user and go to sign-in. /auth/me is
// exempt — the startup check handles its own 401.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const onPublicPage = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/invite'].includes(window.location.pathname);
    if (err.response?.status === 401 && !err.config?.url?.endsWith('/auth/me') && !onPublicPage) {
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ---- Convenience methods ----

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  logoutAll: () => api.post('/auth/logout-all'),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  invitation: (token) => api.get('/auth/invitation', { params: { token } }),
  acceptInvitation: (data) => api.post('/auth/accept-invitation', data),
};

// The signed-in user's organization and team (backend routes/organization.js)
export const orgAPI = {
  get: () => api.get('/organization'),
  rename: (name) => api.put('/organization', { name }),
  invite: (email, role) => api.post('/organization/invitations', { email, role }),
  resendInvite: (id) => api.post(`/organization/invitations/${id}/resend`),
  cancelInvite: (id) => api.delete(`/organization/invitations/${id}`),
  setRole: (id, role) => api.put(`/organization/members/${id}`, { role }),
  removeMember: (id) => api.delete(`/organization/members/${id}`),
  transferOwnership: (user_id, current_password) => api.post('/organization/transfer-ownership', { user_id, current_password }),
};

export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  changeEmail: (data) => api.put('/profile/email', data),
  cancelEmailChange: () => api.delete('/profile/email/pending'),
  changePassword: (data) => api.put('/profile/password', data),
  uploadAvatar: (file) => {
    const form = new FormData();
    form.append('avatar', file);
    return api.post('/profile/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeAvatar: () => api.delete('/profile/avatar'),
  sessions: () => api.get('/profile/sessions'),
  revokeSession: (id) => api.delete(`/profile/sessions/${id}`),
  deleteAccount: (current_password) => api.delete('/profile', { data: { current_password } }),
};

// Superadmin only (backend routes/admin.js)
export const adminAPI = {
  stats: () => api.get('/admin/stats'),
  users: (params) => api.get('/admin/users', { params }),
  user: (id) => api.get(`/admin/users/${id}`),
  // action: approve | reject | suspend | deactivate | reactivate | logout-all | verify-email | resend-verification
  act: (id, action, data) => api.post(`/admin/users/${id}/${action}`, data || {}),
  deleteUser: (id, confirm_email) => api.delete(`/admin/users/${id}`, { data: { confirm_email } }),
  actions: (params) => api.get('/admin/actions', { params }),
  organizations: (params) => api.get('/admin/organizations', { params }),
  organization: (id) => api.get(`/admin/organizations/${id}`),
  // action: approve | reject | suspend | deactivate | reactivate
  actOrg: (id, action, data) => api.post(`/admin/organizations/${id}/${action}`, data || {}),
  deleteOrg: (id, confirm_name) => api.delete(`/admin/organizations/${id}`, { data: { confirm_name } }),
};

export const accountsAPI = {
  list: (params) => api.get('/accounts', { params }),
  create: (data) => api.post('/accounts', data),
  reconnect: (id) => api.put(`/accounts/${id}/reconnect`),
  delete: (id) => api.delete(`/accounts/${id}`),
  platforms: () => api.get('/accounts/platforms'),
  getOAuthUrl: (platform, mode) => api.get(`/accounts/oauth/${platform}`, { params: mode ? { mode } : {} }),
  linkedInSession: (sessionId) => api.get(`/accounts/linkedin/session/${sessionId}`),
  post: (path, data) => api.post(`/accounts${path}`, data),
};

export const groupsAPI = {
  list: (params) => api.get('/groups', { params }),
  get: (id) => api.get(`/groups/${id}`),
  create: (data) => api.post('/groups', data),
  update: (id, data) => api.put(`/groups/${id}`, data),
  delete: (id) => api.delete(`/groups/${id}`),
};

export const postsAPI = {
  list: (params) => api.get('/posts', { params }),
  get: (id) => api.get(`/posts/${id}`),
  calendar: (month) => api.get('/posts/calendar', { params: { month } }),
  summary: () => api.get('/posts/summary'),
  create: (data) => api.post('/posts', data),
  update: (id, data) => api.put(`/posts/${id}`, data),
  delete: (id) => api.delete(`/posts/${id}`),
  bulkCreate: (data) => api.post('/posts/bulk', data),
};

export const mediaAPI = {
  upload: (formData) => api.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: () => api.get('/media'),
  delete: (id) => api.delete(`/media/${id}`),
};

export const analyticsAPI = {
  overview: (days) => api.get('/analytics/overview', { params: { days } }),
  byAccounts: () => api.get('/analytics/publishing-by-accounts'),
};
